import { Card, Col, ConfigProvider, DatePicker, Dropdown, Flex, Form, Modal, Row, Select, Space, Table } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import APIList from '../../http/ApiList';
import { groupBy, sumBy } from 'lodash';
import dayjs from 'dayjs';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@meteor/frontend-core';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { NAME_PATH, useTralitSetting } from '../../hooks/useTralitSetting';
import { AppContext } from '../../contexts/AppContext';
import { ControlOutlined, FilterOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { LabelTag } from '../common/label-tag';
import { buSortMap, segmentSortSeqMap } from '../../contexts/provider/toshiba.provider';
import { EVALUATION_PERIOD } from '../../enum/common.enum';
import { useSnapshot } from '../../hooks/useSnapshot';
import { DraggableTags } from '../common/draggable-tags';
import useStorage from '../../hooks/useStorage';

type ChartProps = {};

const tablePaddingMap = {
  大: 3.5,
  中: 5,
  小: 6,
};

const drillLabels = {
  segment: 'セグメント',
  businessUnit: 'BU',
};

const TopPage: React.FC<ChartProps> = (props) => {
  const navigate = useNavigate();
  const { removeItem } = useStorage();
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const { tralitSettings, color } = useContext(AppContext);
  const { kpiValue, kpiUnitLabel, drillFields, setDrillFields, filter, setFilter, drillFieldsRule } = useContext(ToshibaContext);
  const { currentSnapshot, prefSnapshot, targetSnapshotOptions, snapshotLabel, getMonthLabel } = useSnapshot();
  const [dataProvider, setDataProvider] = useState({
    data: [],
    plan: [],
    prefYear: [],
    target: [],
    reportOverallData: [],
    reportOverallPrefYear: [],
    reportOverallTarget: [],
  });

  const drillDownRule = useMemo(() => ['segment', 'businessUnit'], []);

  const [tableLoading, setTableLoading] = useState(true);

  const [cardHeight, setCardHeight] = useState(0);

  // Model
  const [isSettingOpen, setSettingOpen] = useState(false);

  // form
  const [form] = Form.useForm();

  const curDrillFieldKey = useMemo(() => {
    const lastDrillField = drillFields.at(-1);
    if (lastDrillField) {
      if (drillDownRule.length === drillDownRule.indexOf(lastDrillField.fieldKey)) {
        return drillDownRule.at(-1);
      }
      return drillDownRule[drillDownRule.indexOf(lastDrillField.fieldKey) + 1];
    } else {
      return drillDownRule[0];
    }
  }, [drillDownRule, drillFields]);

  // 下拉框数据
  const options = useMemo(
    () => ({
      snapshots: targetSnapshotOptions,
      fiscalYear: ['FY23', 'FY24', 'FY25', 'FY26', 'FY27'].map((value) => ({ key: value, label: value, value })),
      category: [
        { key: 'segment', label: 'セグメント', value: 'segment' },
        { key: 'businessUnit', label: 'BU', value: 'businessUnit' },
      ],
    }),
    [targetSnapshotOptions]
  );

  // 250825 Add
  // 業績サマリー画面に戻った時にフィルタを初期化する
  // その以外の画面間で遷移する場合はフィルタはリセットしない
  useEffect(() => {
    setDrillFields([]);
    removeItem('ticketDrillDown');
    removeItem('changeDrillDown');
    removeItem('diffDrillDown');
  }, [currentSnapshot]);

  useEffect(() => {
    getData({ fiscalYear: 'FY25', targetSnapshot: filter.targetSnapshot || prefSnapshot });

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          const margin = 16;
          const header = 50;
          const footer = 37;
          const cardHeight = (entry.contentRect.height - margin - header - footer - 50) / 3;
          setCardHeight(Math.floor(cardHeight));
        }
      }
    });
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, [currentSnapshot]);

  const getData = ({ fiscalYear, targetSnapshot }) => {
    setTableLoading(true);
    if (!currentSnapshot || !targetSnapshot) {
      return;
    }
    APIList.getTopData()
      .post({
        fiscalYear,
        currentSnapshot,
        targetSnapshot,
      })
      .then(async (dataResponse) => {
        const defaultFilter = {
          fiscalYear,
          targetSnapshot,
          ...filter,
        };
        setFilter(defaultFilter);
        form.setFieldsValue(defaultFilter);
        setDataProvider(dataResponse);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const render = useCallback(
    (text: number) => (
      <div
        style={{
          textAlign: 'right',
          overflowX: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: `${tablePaddingMap[tralitSettings.fontSize]}px 0`,
          height: cardHeight / 5 - 16,
        }}
      >
        {kpiValue(KPI_CODE.SALES_AMOUNT, text)}
      </div>
    ),
    [tralitSettings.fontSize, cardHeight]
  );

  const diffRender = useCallback(
    (text: number) => (
      <div
        style={{
          textAlign: 'right',
          overflowX: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: `${tablePaddingMap[tralitSettings.fontSize]}px 0`,
        }}
      >
        {kpiValue(KPI_CODE.SALES_AMOUNT, text, true)}
      </div>
    ),
    [tralitSettings.fontSize]
  );

  const actualClickHandler = useCallback(
    (value = undefined) => {
      if (value) {
        setDrillFields((pref) => [...pref, { fieldKey: curDrillFieldKey, value: [value] }]);
      }
      navigate(`/toshiba/achieve-detail?evaluationPeriod=${EVALUATION_PERIOD.ACTUAL_ONLY}`);
    },
    [curDrillFieldKey]
  );

  const forecastClickHandler = useCallback(
    (value = undefined) => {
      if (value) {
        setDrillFields((pref) => [...pref, { fieldKey: curDrillFieldKey, value: [value] }]);
      }
      navigate(`/toshiba/impact-analysis?evaluationPeriod=${EVALUATION_PERIOD.FORECAST_ONLY}`);
    },
    [curDrillFieldKey]
  );

  const psTableData = useMemo(() => {
    const { reportOverallData, reportOverallPrefYear, reportOverallTarget } = dataProvider;
    const psColumns = [
      {
        key: `pcType`,
        dataIndex: 'type',
        title: (
          <div style={{ padding: `${tablePaddingMap[tralitSettings.fontSize]}px 0` }}>
            {drillFields.length > 0 ? drillFields.at(-1).value : 'PSジ'}
          </div>
        ),
        width: 100,
      },
      {
        key: `pcActual`,
        dataIndex: 'actual',
        title: (
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              actualClickHandler();
            }}
          >
            {snapshotLabel.actualLabel}
          </span>
        ),
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `pcForecast`,
        dataIndex: 'forecast',
        title: (
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              forecastClickHandler();
            }}
          >
            {snapshotLabel.forecastLabel}
          </span>
        ),
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `pcTotal`,
        dataIndex: 'total',
        title: '年間総計',
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `pcDiff`,
        dataIndex: 'diff',
        title: '年間差分',
        width: 100,
        className: 'text-right',
        render: diffRender,
      },
    ];

    const [data] = reportOverallData;
    const [prefYear] = reportOverallPrefYear;
    const [target] = reportOverallTarget;

    const psDataSource = [
      {
        type: `今月断面(${getMonthLabel(currentSnapshot)})`,
        actual: data?.salesActuals,
        forecast: data?.salesForecast,
        total: data?.salesActuals + data?.salesForecast,
      },
      {
        type: '予算',
        actual: data?.salesPlanActuals,
        forecast: data?.salesPlanForecast,
        total: data?.salesPlanActuals + data?.salesPlanForecast,
        diff: data?.salesActuals + data?.salesForecast - (data?.salesPlanActuals + data?.salesPlanForecast),
      },
      {
        type: `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
        actual: target?.salesActuals,
        forecast: target?.salesForecast,
        total: target?.salesActuals + target?.salesForecast,
        diff: data?.salesActuals + data?.salesForecast - (target?.salesActuals + target?.salesForecast),
      },
      {
        type: '前年同月',
        actual: prefYear?.salesActuals,
        forecast: prefYear?.salesForecast,
        total: prefYear?.salesActuals + prefYear?.salesForecast,
        diff: data?.salesActuals + data?.salesForecast - (prefYear?.salesActuals + prefYear?.salesForecast),
      },
    ];
    return {
      columns: psColumns,
      dataSource: psDataSource,
    };
  }, [curDrillFieldKey, dataProvider, tralitSettings.fontSize]);

  const [segmentData, setSegmentData] = useState(null);
  const segTablesData = useMemo(() => {
    const gbSegmentData = groupBy(
      dataProvider.data
        // 过滤 国内
        .filter((d) => {
          if (drillFields?.[0]?.value[0] === '原子力') {
            return d.segment === '原子力' && d.businessUnit !== '国内';
          }
          return true;
        })
        .filter((d) => {
          if (drillFields.length === 1) {
            return d[drillFields[0].fieldKey] === drillFields[0].value[0] && d[curDrillFieldKey] !== '調整';
          }
          return true;
        }),
      curDrillFieldKey
    );
    const gbSegmentPlan = groupBy(
      dataProvider.plan
        // 过滤 国内
        .filter((d) => {
          if (drillFields?.[0]?.value[0] === '原子力') {
            return d.segment === '原子力' && d.businessUnit !== '国内';
          }
          return true;
        })
        .filter((d) => {
          if (drillFields.length === 1) {
            return d[drillFields[0].fieldKey] === drillFields[0].value[0] && d[curDrillFieldKey] !== '調整';
          }
          return true;
        }),
      curDrillFieldKey
    );
    const gbSegmentTarget = groupBy(
      dataProvider.target
        // 过滤 国内
        .filter((d) => {
          if (drillFields?.[0]?.value[0] === '原子力') {
            return d.segment === '原子力' && d.businessUnit !== '国内';
          }
          return true;
        })
        .filter((d) => {
          if (drillFields.length === 1) {
            return d[drillFields[0].fieldKey] === drillFields[0].value[0] && d[curDrillFieldKey] !== '調整';
          }
          return true;
        }),
      curDrillFieldKey
    );
    const gbSegmentPrefYear = groupBy(
      dataProvider.prefYear
        // 过滤 国内
        .filter((d) => {
          if (drillFields?.[0]?.value[0] === '原子力') {
            return d.segment === '原子力' && d.businessUnit !== '国内';
          }
          return true;
        })
        .filter((d) => {
          if (drillFields.length === 1) {
            return d[drillFields[0].fieldKey] === drillFields[0].value[0] && d[curDrillFieldKey] !== '調整';
          }
          return true;
        }),
      curDrillFieldKey
    );
    const dataProviders = Object.keys(gbSegmentData)
      .reduce((org, segment) => {
        const segmentData = gbSegmentData[segment] || [];
        const segmentPlan = gbSegmentPlan[segment] || [];
        const segmentTarget = gbSegmentTarget[segment] || [];
        const segmentPrefYear = gbSegmentPrefYear[segment] || [];
        const {
          actual: actualData,
          forecast: forecastData,
          total: totalData,
        } = segmentData.reduce(
          (org, item) => {
            if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
              org.actual.push(item);
            } else {
              org.forecast.push(item);
            }
            org.total.push(item);
            return org;
          },
          {
            actual: [],
            forecast: [],
            total: [],
          }
        );
        const {
          actual: actualPlan,
          forecast: forecastPlan,
          total: totalPlan,
        } = segmentPlan.reduce(
          (org, item) => {
            if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
              org.actual.push(item);
            } else {
              org.forecast.push(item);
            }
            org.total.push(item);
            return org;
          },
          {
            actual: [],
            forecast: [],
            total: [],
          }
        );
        const {
          actual: actualTarget,
          forecast: forecastTarget,
          total: totalTarget,
        } = segmentTarget.reduce(
          (org, item) => {
            if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
              org.actual.push(item);
            } else {
              org.forecast.push(item);
            }
            org.total.push(item);
            return org;
          },
          {
            actual: [],
            forecast: [],
            total: [],
          }
        );
        const {
          actual: actualPrefYear,
          forecast: forecastPrefYear,
          total: totalPrefYear,
        } = segmentPrefYear.reduce(
          (org, item) => {
            if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot).subtract(1, 'year'), 'month')) {
              org.actual.push(item);
            } else {
              org.forecast.push(item);
            }
            org.total.push(item);
            return org;
          },
          {
            actual: [],
            forecast: [],
            total: [],
          }
        );
        const dataSource = [
          {
            type: `今月断面(${getMonthLabel(currentSnapshot)})`,
            actual: sumBy(actualData, KPI_CODE.SALES_AMOUNT),
            forecast: sumBy(forecastData, KPI_CODE.SALES_AMOUNT),
            total: sumBy(totalData, KPI_CODE.SALES_AMOUNT),
          },
          {
            type: '予算',
            actual: sumBy(actualPlan, KPI_CODE.SALES_AMOUNT),
            forecast: sumBy(forecastPlan, KPI_CODE.SALES_AMOUNT),
            total: sumBy(totalPlan, KPI_CODE.SALES_AMOUNT),
            diff: sumBy(totalData, KPI_CODE.SALES_AMOUNT) - sumBy(totalPlan, KPI_CODE.SALES_AMOUNT),
          },
          {
            type: `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
            actual: sumBy(actualTarget, KPI_CODE.SALES_AMOUNT),
            forecast: sumBy(forecastTarget, KPI_CODE.SALES_AMOUNT),
            total: sumBy(totalTarget, KPI_CODE.SALES_AMOUNT),
            diff: sumBy(totalData, KPI_CODE.SALES_AMOUNT) - sumBy(totalTarget, KPI_CODE.SALES_AMOUNT),
          },
          {
            type: '前年同月',
            actual: sumBy(actualPrefYear, KPI_CODE.SALES_AMOUNT),
            forecast: sumBy(forecastPrefYear, KPI_CODE.SALES_AMOUNT),
            total: sumBy(totalPrefYear, KPI_CODE.SALES_AMOUNT),
            diff: sumBy(totalData, KPI_CODE.SALES_AMOUNT) - sumBy(totalPrefYear, KPI_CODE.SALES_AMOUNT),
          },
        ];
        const provider = {
          key: segment,
          columns: [
            {
              key: `${segment}Type`,
              dataIndex: 'type',
              title: (
                <div
                  onClick={() => {
                    if (curDrillFieldKey === 'segment') {
                      setDrillFields((pref) => [...pref, { fieldKey: curDrillFieldKey, value: [segment] }]);
                    }
                  }}
                  style={{
                    cursor: curDrillFieldKey === 'segment' ? 'pointer' : '',
                    textDecoration: curDrillFieldKey === 'segment' ? 'underline' : '',
                    padding: `${tablePaddingMap[tralitSettings.fontSize]}px 0`,
                  }}
                >
                  {segment}
                </div>
              ),
              width: 100,
            },
            {
              key: `${segment}Actual`,
              dataIndex: 'actual',
              title: (
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    actualClickHandler(segment);
                  }}
                >
                  {snapshotLabel.actualLabel}
                </span>
              ),
              width: 100,
              className: 'text-right',
              render,
            },
            {
              key: `${segment}Forecast`,
              dataIndex: 'forecast',
              title: (
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    forecastClickHandler(segment);
                  }}
                >
                  {snapshotLabel.forecastLabel}
                </span>
              ),
              width: 100,
              className: 'text-right',
              render,
            },
            {
              key: `${segment}Total`,
              dataIndex: 'total',
              title: '年間総計',
              width: 100,
              className: 'text-right',
              render,
            },
            {
              key: `pcDiff`,
              dataIndex: 'diff',
              title: '年間差分',
              width: 100,
              className: 'text-right',
              render: diffRender,
            },
          ],
          dataSource,
        };
        org.push(provider);
        return org;
      }, [])
      .sort((a, b) => {
        switch (true) {
          case drillFields.length === 0:
            return segmentSortSeqMap[a.key] - segmentSortSeqMap[b.key];
          case drillFields.length === 1:
            const map = buSortMap[drillFields[0].value];
            return map[a.key] - map[b.key];
          default:
            return 1;
        }
      });
    return dataProviders;
  }, [curDrillFieldKey, dataProvider, tralitSettings.fontSize]);

  useEffect(() => {
    if (curDrillFieldKey === 'segment') {
      setSegmentData(segTablesData);
    }
  }, [curDrillFieldKey, segTablesData]);

  const PSTable = useCallback(() => {
    const dataSource =
      curDrillFieldKey === 'segment' ? psTableData.dataSource : segmentData.find((st) => st.key === drillFields[0].value[0]).dataSource;
    const headerColor = curDrillFieldKey === 'segment' ? '#E90E21' : '#005AC6';
    return (
      <Col span={12}>
        <Card styles={{ body: { padding: 0, minHeight: cardHeight } }}>
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: headerColor,
                  headerColor: '#fff',
                },
              },
            }}
          >
            <Table
              size="small"
              className="small-header border-right-table"
              bordered
              rowHoverable={false}
              rowKey="fieldKey"
              columns={psTableData.columns}
              dataSource={dataSource}
              pagination={false}
              loading={tableLoading}
              // scroll={{
              //   x: 'max-content',
              //   y: tableHeight,
              // }}
            />
          </ConfigProvider>
        </Card>
      </Col>
    );
  }, [tableLoading, psTableData, segmentData]);

  const SegmentTables = useCallback(() => {
    const headerColor = curDrillFieldKey === 'segment' ? '#005AC6' : '#5B3872';
    return (
      <>
        {segTablesData.map(({ key, columns, dataSource }) => (
          <Col key={key} span={12}>
            <Card styles={{ body: { padding: 0, minHeight: cardHeight } }}>
              <ConfigProvider
                theme={{
                  components: {
                    Table: {
                      headerBg: headerColor,
                      headerColor: '#fff',
                    },
                  },
                }}
              >
                <Table
                  size="small"
                  className="small-header border-right-table"
                  bordered
                  rowHoverable={false}
                  rowKey="fieldKey"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  loading={tableLoading}
                  // scroll={{
                  //   x: 'max-content',
                  //   y: tableHeight,
                  // }}
                />
              </ConfigProvider>
            </Card>
          </Col>
        ))}
      </>
    );
  }, [segTablesData, tableLoading]);

  const segmentAdjustment = (dataProvider) => {
    const { data, plan, prefYear, target, reportOverallData, reportOverallPrefYear, reportOverallTarget } = dataProvider;
    const [overallData] = reportOverallData;
    const [overallPrefYear] = reportOverallPrefYear;
    const [overallTarget] = reportOverallTarget;

    const {
      actual: actualData,
      forecast: forecastData,
      total: totalData,
    } = data.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualPlan,
      forecast: forecastPlan,
      total: totalPlan,
    } = plan.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualTarget,
      forecast: forecastTarget,
      total: totalTarget,
    } = target.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualPrefYear,
      forecast: forecastPrefYear,
      total: totalPrefYear,
    } = prefYear.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot).subtract(1, 'year'), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );

    return [
      {
        type: `今月断面(${getMonthLabel(currentSnapshot)})`,
        actual: overallData?.salesActuals - sumBy(actualData, KPI_CODE.SALES_AMOUNT),
        forecast: overallData?.salesForecast - sumBy(forecastData, KPI_CODE.SALES_AMOUNT),
        total: overallData?.salesActuals + overallData?.salesForecast - sumBy(totalData, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: '予算',
        actual: overallData?.salesPlanActuals - sumBy(actualPlan, KPI_CODE.SALES_AMOUNT),
        forecast: overallData?.salesPlanForecast - sumBy(forecastPlan, KPI_CODE.SALES_AMOUNT),
        total: overallData?.salesPlanActuals + overallData?.salesPlanForecast - sumBy(totalPlan, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
        actual: overallTarget?.salesActuals - sumBy(actualTarget, KPI_CODE.SALES_AMOUNT),
        forecast: overallTarget?.salesForecast - sumBy(forecastTarget, KPI_CODE.SALES_AMOUNT),
        total: overallTarget?.salesActuals + overallTarget?.salesForecast - sumBy(totalTarget, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: '前年同月',
        actual: overallPrefYear?.salesActuals - sumBy(actualPrefYear, KPI_CODE.SALES_AMOUNT),
        forecast: overallPrefYear?.salesForecast - sumBy(forecastPrefYear, KPI_CODE.SALES_AMOUNT),
        total: overallPrefYear?.salesActuals + overallPrefYear?.salesForecast - sumBy(totalPrefYear, KPI_CODE.SALES_AMOUNT),
      },
    ];
  };

  const buAdjustment = (dataProvider) => {
    const { data, plan, prefYear, target } = dataProvider;
    const filteredCur = data.filter((d) => d.segment === drillFields[0].value[0] && d.businessUnit === '調整');
    const filteredPlan = plan.filter((d) => d.segment === drillFields[0].value[0] && d.businessUnit === '調整');
    const filteredTar = target.filter((d) => d.segment === drillFields[0].value[0] && d.businessUnit === '調整');
    const filteredPY = prefYear.filter((d) => d.segment === drillFields[0].value[0] && d.businessUnit === '調整');
    const {
      actual: actualData,
      forecast: forecastData,
      total: totalData,
    } = filteredCur.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualPlan,
      forecast: forecastPlan,
      total: totalPlan,
    } = filteredPlan.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualTar,
      forecast: forecastTar,
      total: totalTar,
    } = filteredTar.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    const {
      actual: actualPY,
      forecast: forecastPY,
      total: totalPY,
    } = filteredPY.reduce(
      (org, item) => {
        if (dayjs(item.monthly).isBefore(dayjs(currentSnapshot).subtract(1, 'year'), 'month')) {
          org.actual.push(item);
        } else {
          org.forecast.push(item);
        }
        org.total.push(item);
        return org;
      },
      {
        actual: [],
        forecast: [],
        total: [],
      }
    );
    return [
      {
        type: `今月断面(${getMonthLabel(currentSnapshot)})`,
        actual: sumBy(actualData, KPI_CODE.SALES_AMOUNT),
        forecast: sumBy(forecastData, KPI_CODE.SALES_AMOUNT),
        total: sumBy(totalData, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: '予算',
        actual: sumBy(actualPlan, KPI_CODE.SALES_AMOUNT),
        forecast: sumBy(forecastPlan, KPI_CODE.SALES_AMOUNT),
        total: sumBy(totalPlan, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
        actual: sumBy(actualTar, KPI_CODE.SALES_AMOUNT),
        forecast: sumBy(forecastTar, KPI_CODE.SALES_AMOUNT),
        total: sumBy(totalTar, KPI_CODE.SALES_AMOUNT),
      },
      {
        type: '前年同月',
        actual: sumBy(actualPY, KPI_CODE.SALES_AMOUNT),
        forecast: sumBy(forecastPY, KPI_CODE.SALES_AMOUNT),
        total: sumBy(totalPY, KPI_CODE.SALES_AMOUNT),
      },
    ];
  };

  const AdjustTable = useCallback(() => {
    const adjustColumns = [
      {
        key: `adjustType`,
        dataIndex: 'type',
        title: <div style={{ padding: `${tablePaddingMap[tralitSettings.fontSize]}px 0` }}>調整</div>,
        width: 100,
      },
      {
        key: `adjustActual`,
        dataIndex: 'actual',
        title: (
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              actualClickHandler();
            }}
          >
            {snapshotLabel.actualLabel}
          </span>
        ),
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `adjustForecast`,
        dataIndex: 'forecast',
        title: (
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              forecastClickHandler();
            }}
          >
            {snapshotLabel.forecastLabel}
          </span>
        ),
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `adjustTotal`,
        dataIndex: 'total',
        title: '年間総計',
        width: 100,
        className: 'text-right',
        render,
      },
      {
        key: `pcDiff`,
        dataIndex: 'diff',
        title: '年間差分',
        width: 100,
        className: 'text-right',
        render: diffRender,
      },
    ];
    const adjustDataSource = curDrillFieldKey === 'businessUnit' ? buAdjustment(dataProvider) : segmentAdjustment(dataProvider);
    const headerColor = '#7A7A7A';
    return (
      <Col span={12}>
        <Card styles={{ body: { padding: 0, minHeight: cardHeight } }}>
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: headerColor,
                  headerColor: '#fff',
                },
              },
            }}
          >
            <Table
              size="small"
              className="small-header border-right-table"
              bordered
              rowHoverable={false}
              rowKey="fieldKey"
              columns={adjustColumns}
              dataSource={adjustDataSource}
              pagination={false}
              loading={tableLoading}
              // scroll={{
              //   x: 'max-content',
              //   y: tableHeight,
              // }}
            />
          </ConfigProvider>
        </Card>
      </Col>
    );
  }, [curDrillFieldKey, dataProvider, tableLoading, tralitSettings.fontSize]);

  const HeaderComponent = useCallback(() => {
    const handleOk = () => {
      form.validateFields().then(() => {
        const data = form.getFieldsValue();
        setFilter(data);
        getData(data);
        setSettingOpen(false);
      });
    };

    const onCloseHandler = (fieldKey) => {
      setDrillFields((pref) => pref.filter((item) => item.fieldKey !== fieldKey));
    };

    return (
      <>
        <Col span={24}>
          <Card size="small" styles={{ body: { padding: 0 } }}>
            <Flex style={{ height: '100%', alignItems: 'center', padding: '0px 14px' }}>
              <Flex style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                <span style={{ fontSize: fontSize.title, fontWeight: 'bold' }}>業績サマリー</span>
                <Flex gap="4px 0" wrap style={{ flex: 1 }}>
                  {/* <LabelTag closable={false} fieldKey={'fiscalYear'} label={'比較断面'} value={filter.targetSnapshot} /> */}
                  <LabelTag closable={false} fieldKey={'fiscalYear'} label={'財務年度'} value={filter.fiscalYear} />
                  {drillFields.map((field, index) => {
                    return (
                      <LabelTag
                        closable={drillFields.length - 1 === index}
                        key={field.fieldKey}
                        fieldKey={field.fieldKey}
                        label={drillLabels[field.fieldKey]}
                        value={field.value.join(',')}
                        onClose={() => {
                          onCloseHandler(field.fieldKey);
                        }}
                      />
                    );
                  })}
                </Flex>
              </Flex>
              <Space styles={{ item: { display: 'flex' } }} size={'large'}>
                <Space size={'middle'}>
                  <Space>
                    <div className="legend" style={{ background: '#E90E21' }}></div>
                    <span style={{ fontSize: fontSize.tag }}>事業</span>
                  </Space>
                  <Space>
                    <div className="legend" style={{ background: '#005AC6' }}></div>
                    <span style={{ fontSize: fontSize.tag }}>セグメント</span>
                  </Space>
                  <Space>
                    <div className="legend" style={{ background: '#5B3872' }}></div>
                    <span style={{ fontSize: fontSize.tag }}>BU</span>
                  </Space>
                  <Space>
                    <div className="legend" style={{ background: '#7A7A7A' }}></div>
                    <span style={{ fontSize: fontSize.tag }}>調整</span>
                  </Space>
                </Space>
                <span style={{ fontSize: fontSize.tag }}>単位：{kpiUnitLabel(KPI_CODE.SALES_AMOUNT, {}, false)}</span>
                {!tableLoading && (
                  <ControlOutlined
                    style={{ cursor: 'pointer', fontSize: fontSize.tag, color: color.primaryColor }}
                    onClick={() => {
                      setSettingOpen(true);
                    }}
                  />
                )}
              </Space>
            </Flex>
          </Card>
        </Col>
        <Modal
          width={750}
          title={`受注変動分析`}
          open={isSettingOpen}
          onOk={handleOk}
          onCancel={() => {
            setSettingOpen(false);
          }}
        >
          <Form form={form} labelCol={{ span: 6 }}>
            <Form.Item name="fiscalYear" label={'財務年度'}>
              <Select style={{ width: 120 }} options={options.fiscalYear} />
            </Form.Item>
            <Form.Item name="targetSnapshot" label={'比較断面'}>
              <Select style={{ width: 120 }} options={options.snapshots} />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  }, [filter, fontSize, options, isSettingOpen, tableLoading, drillFields, setDrillFields]);

  return (
    <>
      <Row style={{ height: document.body.clientHeight - 50 - 37 - 16, overflowY: segTablesData.length > 4 ? 'scroll' : null }}>
        {tableLoading ? (
          <Loading />
        ) : (
          <>
            <HeaderComponent />
            {/* <HeaderComp
              {...{
                fontSize,
                filter,
                setFilter,
                drillFieldsRule,
                drillFields,
                setDrillFields,
                getData,
                setSettingOpen,
                isSettingOpen,
                options,
                kpiUnitLabel,
                tableLoading,
                color,
                form,
              }}
            /> */}
            <PSTable />
            <SegmentTables />
            <AdjustTable />
          </>
        )}
      </Row>
    </>
  );
};

export default TopPage;
